import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Copy, Unplug } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const ConnectionRow = ({ c, onRevoke, busy }) => (
  <div className="flex flex-wrap items-center gap-3 py-3 border-b border-zinc-100 last:border-0" data-testid={`mcp-connection-${c.id}`}>
    <div className="min-w-0 flex-1">
      <div className="font-medium text-sm">{c.client_name} · <span className="text-zinc-500">{c.user_email}</span></div>
      <div className="text-[11px] font-mono-accent text-zinc-500">{c.scopes.join(" · ")}</div>
      <div className="text-[11px] text-zinc-500">Connected {fmt(c.created_at)} · Last used {fmt(c.last_used_at)}</div>
    </div>
    {c.revoked_at ? (
      <span className="text-xs text-zinc-400" data-testid={`mcp-connection-revoked-${c.id}`}>Disconnected {fmt(c.revoked_at)}</span>
    ) : (
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onRevoke(c.id)} data-testid={`mcp-connection-revoke-${c.id}`}>
        <Unplug className="w-3.5 h-3.5 mr-1" />{busy ? "Disconnecting…" : "Disconnect"}
      </Button>
    )}
  </div>
);

export const ChatGPTConnectionCard = () => {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const load = useCallback(() => {
    api.get("/mcp-connections").then(({ data }) => setData(data)).catch(() => {});
    api.get("/mcp-connections/audit?limit=15").then(({ data }) => setAudit(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const [busyId, setBusyId] = useState(null);
  const revoke = async (id) => {
    if (!window.confirm("Disconnect ChatGPT? It will lose access immediately.")) return;
    setBusyId(id);
    try {
      await api.post(`/mcp-connections/${id}/revoke`);
      toast.success("ChatGPT disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect. Try again.");
    } finally {
      setBusyId(null);
    }
  };
  const copy = () => { navigator.clipboard?.writeText(data.mcp_url); toast.success("MCP URL copied"); };
  const active = (data?.connections || []).filter((c) => !c.revoked_at);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp mb-8" data-testid="chatgpt-connection-card">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="w-4 h-4 text-[#FF5A00]" />
        <h2 className="font-heading text-xl font-bold">ChatGPT connection</h2>
        <span className={`ml-auto text-xs font-mono-accent ${active.length ? "text-emerald-700" : "text-zinc-500"}`} data-testid="chatgpt-connection-status">
          {active.length ? `${active.length} connected` : "Not connected"}
        </span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Let ChatGPT check availability, read your schedule, book and reschedule appointments, and look up customers.</p>
      {data && (
        <div className="flex items-center gap-2 mb-4">
          <code className="flex-1 min-w-0 truncate text-xs bg-zinc-50 border border-zinc-200 rounded px-3 py-2" data-testid="chatgpt-mcp-url">{data.mcp_url}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid="chatgpt-mcp-url-copy"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
      )}
      <ol className="text-xs text-zinc-600 list-decimal ml-4 space-y-1 mb-4">
        <li>In ChatGPT, open Settings and turn on developer mode if your plan requires it, then add a custom connector or app.</li>
        <li>Paste the MCP URL above and choose OAuth sign-in.</li>
        <li>Sign in to LeadForge as an owner or admin and click Allow.</li>
      </ol>
      {(data?.connections || []).length > 0 && (
        <div className="border-t border-zinc-100 pt-2" data-testid="chatgpt-connections-list">
          {data.connections.map((c) => <ConnectionRow key={c.id} c={c} onRevoke={revoke} busy={busyId === c.id} />)}
        </div>
      )}
      {audit.length > 0 && (
        <details className="mt-4" data-testid="chatgpt-activity">
          <summary className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 cursor-pointer">Recent ChatGPT activity</summary>
          <ul className="mt-2 space-y-1">
            {audit.map((a) => (
              <li key={a.id} className="text-xs flex gap-2" data-testid={`chatgpt-activity-${a.id}`}>
                <span className="text-zinc-400 w-36 shrink-0">{fmt(a.at)}</span>
                <span className={a.ok ? "text-zinc-700" : "text-rose-600"}>{a.tool}{a.error ? ` — ${a.error}` : ""}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
